/**
 * CustomSelect element for variant picker and dropdown selection.
 * Replaces native OS select popups with a fully branded, accessible, keyboard-friendly dropdown.
 */
export class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.init();
  }

  init() {
    this.select = this.querySelector('select');
    this.button = this.querySelector('.custom-select__button');
    this.listbox = this.querySelector('.custom-select__listbox');
    this.buttonText = this.querySelector('.custom-select__button-text');

    if (!this.select || !this.button || !this.listbox) return;

    if (this._initialized) return;
    this._initialized = true;

    // Delegated click handler on component
    this.addEventListener('click', (event) => {
      const option = event.target.closest('.custom-select__option');
      if (option) {
        this.selectOption(option);
        return;
      }

      const btn = event.target.closest('.custom-select__button');
      if (btn) {
        this.toggle();
      }
    });

    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeyDown.bind(this));

    // Close on click outside
    this._onOutsideClick = (event) => {
      if (!this.contains(event.target) && this.isOpen()) {
        this.close(false);
      }
    };
    document.addEventListener('click', this._onOutsideClick);

    // Sync if underlying select changes externally
    this.select.addEventListener('change', () => {
      this.syncFromSelect();
    });
  }

  disconnectedCallback() {
    if (this._onOutsideClick) {
      document.removeEventListener('click', this._onOutsideClick);
    }
  }

  isOpen() {
    return this.button?.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.button || !this.listbox) return;

    // Close any other open custom-selects on the page
    document.querySelectorAll('custom-select').forEach((el) => {
      if (el !== this && typeof el.close === 'function') {
        el.close(false);
      }
    });

    this.setAttribute('data-open', 'true');
    this.button.setAttribute('aria-expanded', 'true');
    this.listbox.removeAttribute('hidden');

    // Focus selected or first option
    const options = Array.from(this.querySelectorAll('.custom-select__option'));
    const selected = this.querySelector('.custom-select__option.is-selected') || options[0];
    if (selected) {
      selected.focus();
    }
  }

  close(focusButton = true) {
    if (!this.button || !this.listbox) return;

    this.removeAttribute('data-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.listbox.setAttribute('hidden', '');

    if (focusButton) {
      this.button.focus();
    }
  }

  selectOption(optionElement) {
    if (!optionElement || !this.select) return;

    const value = optionElement.dataset.value;

    if (this.select.value !== value) {
      this.select.value = value;

      // Update selected attributes on native options
      for (const opt of this.select.options) {
        if (opt.value === value) {
          opt.setAttribute('selected', 'selected');
        } else {
          opt.removeAttribute('selected');
        }
      }

      this.syncUI(value);

      // Dispatch change event on native select so VariantPicker detects it
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.close(true);
  }

  syncFromSelect() {
    if (!this.select) return;
    this.syncUI(this.select.value);
  }

  syncUI(selectedValue) {
    if (this.buttonText) {
      this.buttonText.textContent = selectedValue;
    }

    const options = this.querySelectorAll('.custom-select__option');
    options.forEach((opt) => {
      const isMatch = opt.dataset.value === selectedValue;
      opt.classList.toggle('is-selected', isMatch);
      opt.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
  }

  handleKeyDown(event) {
    const isExpanded = this.isOpen();
    const options = Array.from(this.querySelectorAll('.custom-select__option'));

    switch (event.key) {
      case 'Escape':
        if (isExpanded) {
          event.preventDefault();
          this.close(true);
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!isExpanded) {
          this.open();
        } else {
          this.focusNextOption(options, 1);
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!isExpanded) {
          this.open();
        } else {
          this.focusNextOption(options, -1);
        }
        break;

      case 'Enter':
      case ' ':
        const activeOption = document.activeElement?.closest('.custom-select__option');
        if (activeOption && this.contains(activeOption)) {
          event.preventDefault();
          this.selectOption(activeOption);
        } else if (document.activeElement === this.button) {
          event.preventDefault();
          this.toggle();
        }
        break;

      case 'Tab':
        if (isExpanded) {
          this.close(false);
        }
        break;
    }
  }

  focusNextOption(options, direction) {
    if (!options.length) return;
    const currentFocused = document.activeElement;
    const currentIndex = options.indexOf(currentFocused);
    let nextIndex;

    if (currentIndex === -1) {
      nextIndex = direction > 0 ? 0 : options.length - 1;
    } else {
      nextIndex = (currentIndex + direction + options.length) % options.length;
    }

    options[nextIndex]?.focus();
  }
}

if (!customElements.get('custom-select')) {
  customElements.define('custom-select', CustomSelect);
}
